import { matchesBillingUnit, parseBillingRange } from '@smart-link/shared';
import type {
  BillingLinkUsage,
  BillingRange,
  BillingReport,
  BillingUnit,
  BillingUnitUsage,
} from '@smart-link/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import {
  ArrowDownToLine,
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  FileSpreadsheet,
  LinkIcon,
  Loader2,
  Pencil,
  Plus,
  ReceiptText,
  RefreshCw,
  Search,
  UsersRound,
  X,
} from 'lucide-react';
import { Fragment, useState } from 'react';
import { BillingDateRangePicker } from '../components/BillingDateRangePicker';
import { BillingLinksDialog } from '../components/BillingLinksDialog';
import { useToast } from '../components/Toaster';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardHeader } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { api, downloadApiFile, getToken } from '../lib/api';
import {
  BILLING_PRESETS,
  type BillingPreset,
  billingQuery,
  formatVisits,
  presetRange,
} from '../lib/billing';
import { cn } from '../lib/cn';

interface BillingSearch {
  from?: string;
  to?: string;
  period?: BillingPreset;
  q?: string;
}

export const Route = createFileRoute('/billing')({
  validateSearch: (search: Record<string, unknown>): BillingSearch => {
    const range =
      typeof search.from === 'string' && typeof search.to === 'string'
        ? parseBillingRange(search.from, search.to)
        : null;
    const period = BILLING_PRESETS.find((item) => item.id === search.period)?.id;
    return {
      from: range?.from,
      to: range?.to,
      period: period ?? (range ? 'custom' : undefined),
      q: typeof search.q === 'string' ? search.q.slice(0, 500) : undefined,
    };
  },
  beforeLoad: () => {
    if (!getToken()) throw redirect({ to: '/login' });
  },
  component: BillingPage,
});

function BillingPage() {
  const filters = Route.useSearch();
  const navigate = Route.useNavigate();
  const range = parseBillingRange(filters.from ?? '', filters.to ?? '') ?? presetRange('week');
  const preset = filters.period ?? 'week';
  const search = filters.q ?? '';
  const setSearch = (q: string) =>
    navigate({ search: (previous) => ({ ...previous, q: q || undefined }), replace: true });
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [editing, setEditing] = useState<BillingUnit | 'new' | null>(null);
  const [linking, setLinking] = useState<BillingUnit | null>(null);
  const qc = useQueryClient();
  const report = useQuery({
    queryKey: ['billing-usage', range.from, range.to],
    queryFn: () => api<BillingReport>(`/api/billing/usage?${billingQuery(range.from, range.to)}`),
    refetchInterval: 30_000,
  });
  const rows = (report.data?.rows ?? []).filter((unit) => matchesBillingUnit(unit, search));
  const total = rows.reduce((sum, unit) => sum + unit.visits, 0);
  const linkedCount = rows.reduce((sum, unit) => sum + unit.linkedCount, 0);
  const allExpanded = rows.length > 0 && rows.every((unit) => expanded.has(unit.id));
  const loading = report.isPending || report.isError;

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
          <ReceiptText className="size-5" />
          账单
        </h1>
        <Button onClick={() => setEditing('new')}>
          <Plus />
          新增客户
        </Button>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <BillingDateRangePicker
          range={range}
          preset={preset}
          onChange={(next, selected) =>
            navigate({
              search: (previous) => ({
                ...previous,
                from: next.from,
                to: next.to,
                period: selected,
              }),
              replace: true,
            })
          }
        />
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            title="刷新"
            aria-label="刷新用量"
            disabled={report.isFetching}
            onClick={() => {
              report.refetch();
              qc.invalidateQueries({ queryKey: ['billing-links'] });
            }}
          >
            <RefreshCw className={cn('size-4', report.isFetching && 'animate-spin')} />
          </Button>
          <ExportMenu
            range={range}
            search={search.trim()}
            disabled={!report.data || report.isError || rows.length === 0}
          />
        </div>
      </div>
      <Card className="grid grid-cols-3 gap-0 divide-x py-4 shadow-none">
        <Metric title="计费访问" value={total} loading={loading} accent />
        <Metric title="客户" value={rows.length} loading={loading} />
        <Metric title="链接" value={linkedCount} loading={loading} />
      </Card>
      <Card className="gap-0 overflow-hidden py-0 shadow-none">
        <CardHeader className="flex-row items-center justify-between gap-2 border-b p-3">
          <div className="relative min-w-0 flex-1 sm:max-w-64">
            <Search className="text-muted-foreground absolute top-2.5 left-3 size-4" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="搜索客户"
              maxLength={500}
              aria-label="搜索客户"
              className="pr-8 pl-9"
            />
            {search && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-0.5 right-0.5 size-8"
                title="清空搜索"
                aria-label="清空搜索"
                onClick={() => setSearch('')}
              >
                <X className="size-3.5" />
              </Button>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            disabled={rows.length === 0}
            title={allExpanded ? '收起全部' : '展开全部'}
            aria-label={allExpanded ? '收起全部' : '展开全部'}
            onClick={() =>
              setExpanded((current) => {
                const next = new Set(current);
                for (const unit of rows) allExpanded ? next.delete(unit.id) : next.add(unit.id);
                return next;
              })
            }
          >
            {allExpanded ? <ChevronsDownUp /> : <ChevronsUpDown />}
          </Button>
        </CardHeader>
        {report.isError ? (
          <ErrorState error={report.error} retry={() => report.refetch()} />
        ) : report.isPending ? (
          <LoadingRows />
        ) : rows.length === 0 ? (
          <div className="text-muted-foreground flex flex-col items-center gap-3 px-6 py-14">
            <UsersRound className="size-7 opacity-50" />
            <h2 className="text-sm">{search.trim() ? '无匹配结果' : '暂无客户'}</h2>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/20">
                <TableHead className="w-10">
                  <span className="sr-only">展开</span>
                </TableHead>
                <TableHead>客户</TableHead>
                <TableHead className="text-right">链接</TableHead>
                <TableHead className="text-right">计费访问</TableHead>
                <TableHead className="w-32">
                  <span className="sr-only">操作</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((unit) => (
                <UnitRow
                  key={unit.id}
                  unit={unit}
                  range={range}
                  open={expanded.has(unit.id)}
                  onToggle={() =>
                    setExpanded((current) => {
                      const next = new Set(current);
                      next.has(unit.id) ? next.delete(unit.id) : next.add(unit.id);
                      return next;
                    })
                  }
                  onEdit={() => setEditing(unit)}
                  onManageLinks={() => setLinking(unit)}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      {editing && (
        <UnitDialog
          unit={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['billing-usage'] });
            qc.invalidateQueries({ queryKey: ['billing-units'] });
          }}
        />
      )}
      {linking && (
        <BillingLinksDialog key={linking.id} unit={linking} onClose={() => setLinking(null)} />
      )}
    </div>
  );
}

function Metric({
  title,
  value,
  loading,
  accent,
}: { title: string; value: number; loading: boolean; accent?: boolean }) {
  return (
    <div className="space-y-1 px-4 sm:px-5">
      <div className="text-muted-foreground text-xs">{title}</div>
      {loading ? (
        <Skeleton className="h-7 w-16" />
      ) : (
        <div
          className={cn(
            'text-2xl font-semibold tracking-tight tabular-nums',
            accent && 'text-success',
          )}
        >
          {formatVisits(value)}
        </div>
      )}
    </div>
  );
}

function UnitRow({
  unit,
  range,
  open,
  onToggle,
  onEdit,
  onManageLinks,
}: {
  unit: BillingUnitUsage;
  range: BillingRange;
  open: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onManageLinks: () => void;
}) {
  return (
    <Fragment>
      <TableRow className={cn(open && 'bg-muted/20')}>
        <TableCell className="pr-0 pl-3">
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            title={open ? '收起' : '展开'}
            aria-label={`${open ? '收起' : '展开'}${unit.name}的链接`}
            aria-expanded={open}
            aria-controls={`unit-links-${unit.id}`}
            onClick={onToggle}
          >
            {open ? <ChevronDown /> : <ChevronRight />}
          </Button>
        </TableCell>
        <TableCell className="max-w-sm">
          <Button
            variant="link"
            className="h-auto max-w-full justify-start p-0 font-medium"
            onClick={onToggle}
            aria-expanded={open}
          >
            <span className="truncate">{unit.name}</span>
          </Button>
          {unit.note && (
            <p className="text-muted-foreground mt-1 truncate text-xs" title={unit.note}>
              {unit.note}
            </p>
          )}
        </TableCell>
        <TableCell className="text-right tabular-nums">
          <Button
            variant="link"
            className="h-auto p-0 tabular-nums"
            title="关联链接"
            aria-label={`管理${unit.name}的关联链接`}
            onClick={onManageLinks}
          >
            {formatVisits(unit.linkedCount)}
          </Button>
        </TableCell>
        <TableCell className="text-success text-right font-medium tabular-nums">
          {formatVisits(unit.visits)}
        </TableCell>
        <TableCell className="pl-0">
          <div className="flex justify-end gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              title="关联链接"
              aria-label={`关联${unit.name}的链接`}
              onClick={onManageLinks}
            >
              <LinkIcon className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              title="编辑客户"
              aria-label={`编辑${unit.name}`}
              onClick={onEdit}
            >
              <Pencil className="size-3.5" />
            </Button>
            <ExportMenu range={range} unit={unit} compact />
          </div>
        </TableCell>
      </TableRow>
      {open && (
        <TableRow className="bg-muted/20 hover:bg-muted/20">
          <TableCell colSpan={5} className="p-0">
            <div id={`unit-links-${unit.id}`} className="px-4 py-3 sm:px-8">
              <LinkDetails unit={unit} range={range} onManageLinks={onManageLinks} />
            </div>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

function LinkDetails({
  unit,
  range,
  onManageLinks,
}: { unit: BillingUnitUsage; range: BillingRange; onManageLinks: () => void }) {
  const details = useQuery({
    queryKey: ['billing-links', unit.id, range.from, range.to],
    queryFn: () =>
      api<{ rows: BillingLinkUsage[] }>(
        `/api/billing/units/${unit.id}/links?${billingQuery(range.from, range.to)}`,
      ),
    refetchInterval: 30_000,
  });
  if (details.isError) return <ErrorState error={details.error} retry={() => details.refetch()} />;
  if (details.isPending) return <LoadingRows />;
  if (details.data.rows.length === 0)
    return (
      <div className="text-muted-foreground flex items-center justify-center gap-3 py-5 text-xs">
        暂无链接
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          title="关联链接"
          aria-label={`关联${unit.name}的链接`}
          onClick={onManageLinks}
        >
          <LinkIcon className="size-3.5" />
        </Button>
      </div>
    );
  return (
    <div className="bg-background overflow-hidden rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>链接</TableHead>
            <TableHead className="text-right">计费访问</TableHead>
            <TableHead className="w-12 text-right">
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                title="关联链接"
                aria-label={`关联${unit.name}的链接`}
                onClick={onManageLinks}
              >
                <LinkIcon className="size-3.5" />
              </Button>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {details.data.rows.map((link) => (
            <TableRow key={link.linkId}>
              <TableCell className="max-w-xs">
                <div className="flex items-center gap-2">
                  {link.deleted ? (
                    <span className="text-muted-foreground truncate">{link.linkName}</span>
                  ) : (
                    <Link
                      to="/link/$id"
                      params={{ id: link.linkId }}
                      className="truncate font-medium hover:underline"
                      title={link.linkName}
                    >
                      {link.linkName}
                    </Link>
                  )}
                  {(link.deleted || !link.linked) && (
                    <Badge variant="outline" className="shrink-0 text-[10px]">
                      {link.deleted ? '已删除' : '历史'}
                    </Badge>
                  )}
                </div>
                <code className="text-muted-foreground mt-1 block text-xs">/{link.linkId}</code>
              </TableCell>
              <TableCell className="text-success text-right font-medium tabular-nums">
                {formatVisits(link.visits)}
              </TableCell>
              <TableCell className="text-right">
                {!link.deleted && (
                  <Button asChild variant="ghost" size="icon" className="size-7">
                    <Link
                      to="/link/$id"
                      params={{ id: link.linkId }}
                      title="链接详情"
                      aria-label={`打开${link.linkName}`}
                    >
                      <ArrowUpRight className="size-4" />
                    </Link>
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ExportMenu({
  range,
  unit,
  search = '',
  disabled = false,
  compact = false,
}: {
  range: BillingRange;
  unit?: BillingUnit;
  search?: string;
  disabled?: boolean;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  async function download(groupBy: 'unit' | 'link') {
    setBusy(true);
    const params = billingQuery(range.from, range.to);
    params.set('groupBy', groupBy);
    if (unit) params.set('unitId', unit.id);
    if (search) params.set('q', search);
    try {
      await downloadApiFile(
        `/api/billing/export?${params}`,
        `账单${groupBy === 'unit' ? '客户汇总' : '链接明细'}-${range.from}-${range.to}.csv`,
      );
      setOpen(false);
      toast.success('已导出');
    } catch (error) {
      toast.error('导出失败', error instanceof Error ? error.message : '请求失败');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={compact ? 'size-8' : undefined}
          disabled={disabled || busy}
          title="导出 CSV"
          aria-label={unit ? `导出${unit.name}` : '导出当前筛选'}
        >
          {busy ? (
            <Loader2 className="animate-spin" />
          ) : (
            <ArrowDownToLine className={compact ? 'size-3.5' : 'size-4'} />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-40 space-y-1 p-1">
        <Button
          variant="ghost"
          className="w-full justify-start"
          disabled={busy}
          onClick={() => download('unit')}
        >
          <FileSpreadsheet />
          客户汇总
        </Button>
        <Button
          variant="ghost"
          className="w-full justify-start"
          disabled={busy}
          onClick={() => download('link')}
        >
          <LinkIcon />
          链接明细
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function UnitDialog({
  unit,
  onClose,
  onSaved,
}: { unit?: BillingUnit; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(unit?.name ?? '');
  const [note, setNote] = useState(unit?.note ?? '');
  const toast = useToast();
  const save = useMutation({
    mutationFn: () =>
      api(unit ? `/api/billing/units/${unit.id}` : '/api/billing/units', {
        method: unit ? 'PUT' : 'POST',
        body: JSON.stringify({ name: name.trim(), note: note.trim() }),
      }),
    onSuccess: () => {
      onSaved();
      onClose();
      toast.success('已保存');
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !save.isPending) onClose();
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        onInteractOutside={(event) => {
          if (save.isPending) event.preventDefault();
        }}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim() && !save.isPending) save.mutate();
          }}
          className="space-y-5"
        >
          <DialogHeader>
            <DialogTitle>{unit ? '编辑客户' : '新增客户'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="unit-name">名称</Label>
            <Input
              id="unit-name"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={128}
              disabled={save.isPending}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="unit-note">备注</Label>
            <Input
              id="unit-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              maxLength={500}
              disabled={save.isPending}
            />
          </div>
          {save.isError && (
            <p role="alert" className="text-destructive text-sm">
              {save.error instanceof Error ? save.error.message : '保存失败'}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={save.isPending} onClick={onClose}>
              取消
            </Button>
            <Button type="submit" disabled={!name.trim() || save.isPending}>
              {save.isPending && <Loader2 className="animate-spin" />}保存
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function LoadingRows() {
  return (
    <div className="space-y-4 p-5" aria-live="polite" aria-label="加载中">
      {[1, 2, 3].map((key) => (
        <div key={key} className="flex justify-between gap-6">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-14" />
          <Skeleton className="h-4 w-20" />
        </div>
      ))}
    </div>
  );
}

function ErrorState({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <div role="alert" className="flex items-center justify-center gap-3 p-8">
      <p className="text-destructive text-sm">
        {error instanceof Error ? error.message : '加载失败'}
      </p>
      <Button variant="ghost" size="icon" title="重试" aria-label="重试" onClick={retry}>
        <RefreshCw />
      </Button>
    </div>
  );
}
